import { Component } from '@theme/component';

/**
 * MapLibre's dist is ~276KB gzipped — several times the rest of the theme's
 * JavaScript combined. It is loaded on intersection rather than on page load so
 * a visitor who never scrolls to the map never pays for it, and the library is
 * fetched once per page no matter how many map sections a template renders.
 *
 * @type {Promise<any> | null}
 */
let libraryPromise = null;

/**
 * Injects the vendored MapLibre script and stylesheet.
 *
 * @param {string} scriptUrl - Asset URL for maplibre-gl.js.
 * @param {string} stylesheetUrl - Asset URL for maplibre-gl.css.
 * @returns {Promise<any>} Resolves with the maplibregl global.
 */
function loadMapLibre(scriptUrl, stylesheetUrl) {
  libraryPromise ??= new Promise((resolve, reject) => {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = stylesheetUrl;
    document.head.append(stylesheet);

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.addEventListener('load', () => {
      // @ts-ignore - the UMD bundle assigns itself to the global scope.
      const maplibregl = window.maplibregl;
      if (maplibregl) resolve(maplibregl);
      else reject(new Error('MapLibre loaded but did not register a global'));
    });
    script.addEventListener('error', () => reject(new Error('MapLibre failed to load')));
    document.head.append(script);
  });

  return libraryPromise;
}

/**
 * Requests the style JSON, and the TileJSON its sources point at, so those
 * round trips overlap the library download instead of queueing behind it.
 * MapLibre can't ask for the style until 276KB of JavaScript has arrived and
 * parsed, and it can't ask for a tile until the style tells it where they live,
 * so a cold map is three serial round trips deep before it draws anything.
 *
 * This doesn't hand the result to MapLibre — it warms the HTTP cache, and
 * MapLibre makes its own requests as usual. OpenFreeMap serves both with
 * `cache-control: public, max-age=86400`, so those requests are served from the
 * cache. Passing a parsed style object instead would skip a fetch outright, but
 * it would also change how relative URLs inside a style resolve, and the style
 * URL is an editor-facing setting that can point anywhere.
 *
 * Purely an optimisation, so every failure is swallowed: the worst case is that
 * MapLibre fetches what it would have fetched anyway.
 *
 * @param {string} styleUrl - The MapLibre style JSON URL.
 * @returns {Promise<void>}
 */
async function warmTileHost(styleUrl) {
  try {
    const response = await fetch(styleUrl);
    const style = await response.json();

    await Promise.all(
      Object.values(style?.sources ?? {})
        .map((source) => source?.url)
        .filter((url) => typeof url === 'string')
        .map((url) => fetch(url))
    );
  } catch {
    // Nothing to report or retry — MapLibre requests these itself.
  }
}

/**
 * One destination, as emitted by snippets/travel-map-destinations.liquid.
 *
 * @typedef {object} Pin
 * @property {string} name
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} [region] - The US state.
 * @property {string} [campground]
 * @property {string} [summary]
 * @property {string} [url] - The YouTube episode featuring the stop.
 * @property {string} [articleUrl]
 * @property {string} [articleTitle]
 * @property {string} [galleryUrl] - This stop's bucket on the gallery page. Only
 *   set when the destination actually has photos.
 */

/**
 * Renders travel destinations on an interactive map.
 *
 * The visible destination list beside the map is the accessible equivalent and
 * is rendered server-side, so this component is purely additive: with
 * JavaScript off, or if the tile host is unreachable, the content still reads.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} canvas - The element MapLibre mounts into.
 * @property {HTMLScriptElement} data - JSON script tag holding the pins.
 * @property {HTMLTemplateElement} [episodeIcon] - The YouTube icon, cloned per popup.
 *
 * @extends Component<Refs>
 */
class TravelMapComponent extends Component {
  requiredRefs = ['canvas', 'data'];

  /** @type {any} */
  #map = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    const pins = this.#readPins();
    if (pins.length === 0) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#render(pins);
      },
      // A generous margin, because what follows is a 276KB download and three
      // round trips to the tile host: starting roughly a phone screen early is
      // the difference between the map being drawn when it arrives on screen
      // and it drawing while the visitor watches. Still gated on approaching
      // the section, so a visitor who never scrolls this far pays nothing.
      { rootMargin: '800px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#map?.remove();
    this.#map = null;
  }

  /**
   * @returns {Array<Pin>}
   */
  #readPins() {
    try {
      const pins = JSON.parse(this.refs.data.textContent || '[]');
      return Array.isArray(pins) ? pins : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {Array<Pin>} pins
   */
  async #render(pins) {
    const { scriptUrl, stylesheetUrl, styleUrl } = this.dataset;
    if (!scriptUrl || !stylesheetUrl || !styleUrl) return;

    // Not awaited: it runs alongside the library download rather than in front
    // of the map, and awaiting it would put a slow tile host back on the
    // critical path — the thing it exists to take off it.
    void warmTileHost(styleUrl);

    let maplibregl;
    try {
      maplibregl = await loadMapLibre(scriptUrl, stylesheetUrl);
    } catch {
      // The server-rendered list is still on the page, so failing quietly
      // leaves a usable section rather than an error the visitor can't act on.
      // Give back the space the canvas was holding for a map that isn't coming.
      this.dataset.mapState = 'unavailable';
      return;
    }

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    let map;
    try {
      map = new maplibregl.Map({
        container: this.refs.canvas,
        style: styleUrl,
        // Scroll-to-zoom would otherwise swallow page scrolling when the map
        // fills the viewport on a phone. Cooperative gestures require a
        // modifier key or a two-finger gesture instead.
        cooperativeGestures: true,
        attributionControl: { compact: true },
      });
    } catch {
      // MapLibre throws from the constructor when it can't get a WebGL
      // context. Collapse the reserved space so those visitors get the
      // destination list on its own rather than an empty grey rectangle.
      this.dataset.mapState = 'unavailable';
      return;
    }

    this.#map = map;

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', () => {
      const pinColor = getComputedStyle(this).getPropertyValue('--travel-map-pin-color').trim();
      const bounds = new maplibregl.LngLatBounds();

      const newWindowLabel = this.dataset.newWindowLabel ?? '';

      for (const pin of pins) {
        // Cloned per pin: a fragment can only be inserted once, so a shared one
        // would land in the last popup and leave the rest without an icon.
        const icon = this.refs.episodeIcon?.content.cloneNode(true) ?? null;

        new maplibregl.Marker({ color: pinColor || undefined })
          .setLngLat([pin.longitude, pin.latitude])
          .setPopup(
            new maplibregl.Popup({ offset: 24, closeButton: false }).setDOMContent(
              buildPopup(pin, icon, newWindowLabel)
            )
          )
          .addTo(map);

        bounds.extend([pin.longitude, pin.latitude]);
      }

      // The canvas was already at its full height when the map was constructed,
      // so this reveals a correctly-sized map rather than resizing one into
      // place: no `resize()` before fitting, and no reflow of the section.
      this.dataset.mapState = 'ready';

      this.#applyInitialView(map, bounds, reducedMotion);
    });
  }

  /**
   * @param {any} map
   * @param {any} bounds
   * @param {boolean} reducedMotion
   */
  #applyInitialView(map, bounds, reducedMotion) {
    const { initialView, centerLatitude, centerLongitude, zoom } = this.dataset;

    if (initialView === 'custom' && centerLatitude && centerLongitude) {
      map.jumpTo({
        center: [Number(centerLongitude), Number(centerLatitude)],
        zoom: Number(zoom) || 4,
      });
      return;
    }

    // A single pin has zero-area bounds, which fitBounds resolves to maximum
    // zoom — close enough to read individual buildings. Pick a regional zoom
    // instead so one destination looks deliberate rather than broken.
    if (bounds.getNorthEast().toString() === bounds.getSouthWest().toString()) {
      map.jumpTo({ center: bounds.getCenter(), zoom: Number(zoom) || 6 });
      return;
    }

    map.fitBounds(bounds, { padding: 48, animate: !reducedMotion, maxZoom: 9 });
  }
}

/**
 * Builds popup content as DOM rather than an HTML string, so destination copy
 * entered in Admin can never be parsed as markup.
 *
 * Every field is optional except the name, because a destination can be plotted
 * before its campground details or episode exist.
 *
 * @param {Pin} pin
 * @param {DocumentFragment | null} [episodeIcon] - A clone of the icon template.
 * @param {string} [newWindowLabel] - Translated "opens in a new window" notice,
 *   passed in because Liquid's `t` filter isn't reachable from here.
 * @returns {HTMLElement}
 */
function buildPopup(pin, episodeIcon = null, newWindowLabel = '') {
  const wrapper = document.createElement('div');
  wrapper.className = 'travel-map__popup';

  wrapper.append(paragraph('travel-map__popup-name', pin.name));

  if (pin.region) wrapper.append(paragraph('travel-map__popup-region', pin.region));
  if (pin.campground) wrapper.append(paragraph('travel-map__popup-campground', pin.campground));
  if (pin.summary) wrapper.append(paragraph('travel-map__popup-summary', pin.summary));

  if (pin.url || pin.articleUrl || pin.galleryUrl) {
    const links = document.createElement('p');
    links.className = 'travel-map__popup-links';

    if (pin.url) {
      links.append(episodeLink(pin.url, pin.name, episodeIcon, newWindowLabel));
    }

    if (pin.articleUrl) {
      links.append(anchor('travel-map__popup-link', pin.articleUrl, pin.articleTitle || 'Read the post'));
    }

    // Wording kept in step with the destination list in
    // `travel-map-destinations.liquid`: the list is this popup's accessible
    // equivalent, so the two must announce the same link the same way.
    if (pin.galleryUrl) {
      links.append(anchor('travel-map__popup-link', pin.galleryUrl, 'Photos', pin.name, 'from'));
    }

    wrapper.append(links);
  }

  return wrapper;
}

/**
 * @param {string} className
 * @param {string} text
 * @returns {HTMLParagraphElement}
 */
function paragraph(className, text) {
  const element = document.createElement('p');
  element.className = className;
  element.textContent = text;
  return element;
}

/**
 * @param {string} className
 * @param {string} href
 * @param {string} text
 * @param {string} [context] - Appended for screen readers, so a list of
 *   identically-labelled links still says which destination each belongs to.
 * @param {string} [connector] - Joins the label to the context. A parameter
 *   because "Photos from Moab" and "Read the post about Moab" both need to read
 *   as a sentence.
 * @returns {HTMLAnchorElement}
 */
function anchor(className, href, text, context, connector = 'about') {
  const link = document.createElement('a');
  link.className = className;
  link.href = href;
  link.textContent = text;

  if (context) {
    const suffix = document.createElement('span');
    suffix.className = 'visually-hidden';
    suffix.textContent = ` ${connector} ${context}`;
    link.append(suffix);
  }

  return link;
}

/**
 * The episode link, as an icon.
 *
 * The label is visually hidden rather than dropped: an icon-only link with no
 * accessible name is announced as just its URL, and "youtube.com/watch?v=…"
 * doesn't tell a screen reader user what they're about to open.
 *
 * @param {string} href
 * @param {string} name - The destination, for the accessible name.
 * @param {DocumentFragment | null} icon - A clone of the section's icon template.
 * @param {string} [newWindowLabel] - Translated "opens in a new window" notice.
 * @returns {HTMLAnchorElement}
 */
function episodeLink(href, name, icon, newWindowLabel = '') {
  const link = document.createElement('a');
  link.className = 'travel-map__popup-link travel-map__popup-link--episode';
  link.href = href;

  // YouTube is off the store entirely, so the episode opens in a new tab and
  // the visitor keeps the map. `rel` matches the theme's other external links.
  link.target = '_blank';
  link.rel = 'noopener noreferrer';

  const label = document.createElement('span');
  label.className = 'visually-hidden';

  // Falling back to text keeps the link usable if the template is ever missing,
  // rather than rendering an anchor with nothing to click.
  if (icon) {
    label.textContent = `Watch the episode about ${name}. ${newWindowLabel}`.trim();
    link.append(icon, label);
  } else {
    label.textContent = ` about ${name}. ${newWindowLabel}`.trimEnd();
    link.append(document.createTextNode('Watch the episode'), label);
  }

  return link;
}

if (!customElements.get('travel-map')) {
  customElements.define('travel-map', TravelMapComponent);
}
